import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Popover from '@mui/material/Popover'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
/* eslint-disable import/no-duplicates */
import ptBR from 'date-fns/locale/pt-BR'
import {
  format,
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  startOfWeek,
  endOfWeek,
} from 'date-fns'
/* eslint-enable import/no-duplicates */

import Iconify from 'src/components/iconify'

// ----------------------------------------------------------------------

export type DateTimeRangePickerProps = {
  startDate: Date | null
  endDate: Date | null
  onChange: (start: Date | null, end: Date | null) => void
  label?: string
  size?: 'small' | 'medium'
  fullWidth?: boolean
  placeholder?: string
}

export default function DateTimeRangePicker({
  startDate,
  endDate,
  onChange,
  label = 'Período',
  size = 'medium',
  fullWidth = true,
  placeholder = 'Todo o período',
}: DateTimeRangePickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [tempStart, setTempStart] = useState<Date | null>(startDate)
  const [tempEnd, setTempEnd] = useState<Date | null>(endDate)

  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTempStart(startDate)
    setTempEnd(endDate)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleApply = () => {
    onChange(tempStart, tempEnd)
    handleClose()
  }

  const handleClear = () => {
    setTempStart(null)
    setTempEnd(null)
    onChange(null, null)
    handleClose()
  }

  const applyPreset = (start: Date, end: Date) => {
    setTempStart(start)
    setTempEnd(end)
    onChange(start, end)
    handleClose()
  }

  const presets = useMemo(() => {
    const now = new Date()
    return [
      { label: 'Hoje', start: startOfDay(now), end: endOfDay(now) },
      {
        label: 'Ontem',
        start: startOfDay(subDays(now, 1)),
        end: endOfDay(subDays(now, 1)),
      },
      { label: 'Últimos 7 dias', start: startOfDay(subDays(now, 6)), end: endOfDay(now) },
      { label: 'Últimos 30 dias', start: startOfDay(subDays(now, 29)), end: endOfDay(now) },
      {
        label: 'Esta semana',
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
      },
      { label: 'Este mês', start: startOfMonth(now), end: endOfMonth(now) },
    ]
  }, [])

  const formatLabel = () => {
    if (!startDate && !endDate) return placeholder
    const fmt = (d: Date | null) => (d ? format(d, "dd/MM/yyyy 'às' HH:mm") : '—')
    return `${fmt(startDate)}  →  ${fmt(endDate)}`
  }

  const hasValue = Boolean(startDate || endDate)

  return (
    <>
      <TextField
        fullWidth={fullWidth}
        size={size}
        label={label}
        value={formatLabel()}
        onClick={handleOpen}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position='start'>
              <Iconify icon='solar:calendar-bold-duotone' width={20} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position='end'>
              {hasValue && (
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation()
                    onChange(null, null)
                  }}
                >
                  <Iconify icon='eva:close-fill' width={18} />
                </IconButton>
              )}
              <Iconify icon='eva:arrow-ios-downward-fill' width={18} />
            </InputAdornment>
          ),
          sx: { cursor: 'pointer' },
        }}
        sx={{ '& input': { cursor: 'pointer' } }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 1, borderRadius: 2, width: { xs: '92vw', md: 560 } } }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} divider={<Divider flexItem />}>
          <Stack sx={{ p: 2, minWidth: { md: 180 } }} spacing={0.5}>
            <Typography variant='caption' color='text.secondary' sx={{ mb: 0.5 }}>
              Atalhos
            </Typography>
            {presets.map((p) => (
              <Button
                key={p.label}
                size='small'
                variant='text'
                onClick={() => applyPreset(p.start, p.end)}
                sx={{ justifyContent: 'flex-start', fontWeight: 500 }}
              >
                {p.label}
              </Button>
            ))}
          </Stack>

          <Stack sx={{ p: 2, flex: 1 }} spacing={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
              <DateTimePicker
                label='Início'
                value={tempStart}
                onChange={(v) => setTempStart(v)}
                maxDateTime={tempEnd || undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
              <DateTimePicker
                label='Fim'
                value={tempEnd}
                onChange={(v) => setTempEnd(v)}
                minDateTime={tempStart || undefined}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </LocalizationProvider>

            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button size='small' color='inherit' onClick={handleClear}>
                Limpar
              </Button>
              <Button size='small' onClick={handleClose}>
                Cancelar
              </Button>
              <Button size='small' variant='contained' onClick={handleApply}>
                Aplicar
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  )
}
